import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { MovieCard } from "@/components/MovieCard";
import { PageToolbar, usePageTranslation } from "@/components/PageToolbar";

export default function PersonDetail() {
  const { id } = useParams();
  const [person, setPerson] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPerson(null);
    api.get(`/people/${id}`).then((r) => setPerson(r.data));
  }, [id]);

  const blocks = useMemo(() => (person ? { bio: person.bio } : null), [person]);
  const { lang, tr, busy } = usePageTranslation(blocks, `person-${id}`);

  if (!person) return <main className="max-w-7xl mx-auto px-6 py-24 text-center font-display text-2xl text-[#6b5d51]">Loading…</main>;

  return (
    <main data-testid="person-detail-page">
      <section className="bg-[#171210] text-[#f6f1e7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <p className="eyebrow" style={{ color: "#e8b96a" }}>{person.roles.join(" · ")}</p>
          <h1 data-testid="person-name" className="font-display font-bold text-4xl sm:text-6xl mt-2">{person.name}</h1>
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-[#d6cebf]">
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {person.birthplace}</span>
            <span>b. {person.born}{person.died ? ` — d. ${person.died}` : ""}</span>
            {person.hall_of_fame && (
              <span className="flex items-center gap-1.5 text-[#e8b96a]"><Trophy className="w-4 h-4" /> MoviePulse Hall of Fame</span>
            )}
          </div>
          <p data-testid="person-bio" className="mt-6 text-lg leading-relaxed max-w-3xl text-[#e9e2d4]">{tr("bio", person.bio)}</p>
          <div className="mt-6">
            <PageToolbar speakText={`${person.name}. ${tr("bio", person.bio)}`} busy={busy} lang={lang} />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-end justify-between mb-6 rule-thick pt-4">
          <h2 className="font-display font-bold text-2xl">Filmography on MoviePulse</h2>
          <Link to={`/country/${person.country_code}`} data-testid="person-country-link" className="text-sm font-medium link-underline text-[#7d1a1a]">
            {person.country} cinema →
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {person.filmography.map((m) => <MovieCard key={m.id} movie={m} />)}
        </div>
        {person.filmography.length === 0 && <p className="text-[#6b5d51]">Credits are being verified and indexed.</p>}
      </section>
    </main>
  );
}
